/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {TextInput} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {useMutation} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {Layout} from 'components/DSYS/Layout';
import {Link} from 'components/DSYS/Link';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {ReactNode, useState} from 'react';
import {useNavigate} from 'react-router';
import {getBackendUrl} from 'utilities';

type AmplitudeLookupMatch = {
  user_id?: string | null;
  name?: string | null;
  email?: string | null;
  tenant_id?: string | null;
  ldap?: string | null;
  is_deactivated?: boolean | null;
};

type AmplitudeLookupResponse = {
  query: string;
  found: boolean;
  match_type: 'employee' | 'user' | 'external_api_app' | 'server' | 'not_found';
  match?: AmplitudeLookupMatch | null;
  message?: string | null;
};

const lookupAmplitudeId = async (hashedId: string): Promise<AmplitudeLookupResponse> => {
  const response = await fetch(`${getBackendUrl()}/api/v1/admin/amplitude/lookup`, {
    method: 'POST',
    credentials: 'include',
    headers: {Accept: 'application/json', 'Content-Type': 'application/json'},
    body: JSON.stringify({hashed_id: hashedId}),
  });

  let payload: AmplitudeLookupResponse;
  try {
    payload = (await response.json()) as AmplitudeLookupResponse;
  } catch {
    throw new Error(response.statusText || 'Request failed');
  }

  if (!response.ok) {
    const message =
      typeof payload === 'object' && payload && 'message' in payload && payload.message
        ? payload.message
        : response.statusText || 'Request failed';
    throw new Error(message);
  }

  return payload;
};

const ResultRow = ({label, value}: {label: string; value: ReactNode}) => (
  <Box style={{display: 'flex', justifyContent: 'space-between', gap: 12}}>
    <Text color="subtle">{label}</Text>
    {typeof value === 'string' ? <Text isBold>{value}</Text> : value}
  </Box>
);

const LookupResult = ({result}: {result: AmplitudeLookupResponse}) => {
  const match = result.match;

  return (
    <Box
      borderRadius="Large"
      borderWidth="1"
      borderStyle="Solid"
      borderColor="Border Subtle"
      padding="16"
      backgroundColor="Background Base"
    >
      <Stack gap="8">
        {match?.name ? (
          <ResultRow
            label="Name"
            value={
              match.ldap ? (
                <Link to={ROUTE_PATHS.PROFILE.replace(':userid', match.ldap)} isBold>
                  {match.name}
                </Link>
              ) : (
                match.name
              )
            }
          />
        ) : null}
        {match?.email ? <ResultRow label="Email" value={match.email} /> : null}
        {match?.ldap ? (
          <ResultRow
            label="Slack"
            value={
              <Link to={`https://dropbox.slack.com/team/${match.ldap}`} isBold>
                @{match.ldap}
              </Link>
            }
          />
        ) : null}
        {match?.user_id ? <ResultRow label="User ID" value={match.user_id} /> : null}
        {result.message ? (
          <Text color={result.found ? 'standard' : 'subtle'} size="small">
            {result.message}
          </Text>
        ) : null}
      </Stack>
    </Box>
  );
};

const AmplitudeLookupPanel = () => {
  const [hashedId, setHashedId] = useState('');

  const lookupMutation = useMutation({
    mutationFn: () => lookupAmplitudeId(hashedId.trim()),
  });

  const result = lookupMutation.data;
  const canSubmit = hashedId.trim().length > 0 && !lookupMutation.isPending;

  return (
    <Stack gap="12">
      <Text color="subtle">
        Amplitude user ids are a SHA-256 hash of the user&apos;s email. Paste one below to find the
        matching person. This resolves PII and is logged as a superuser action.
      </Text>

      <Box
        borderRadius="Large"
        borderWidth="1"
        borderStyle="Solid"
        borderColor="Border Subtle"
        padding="16"
        backgroundColor="Background Base"
      >
        <Stack gap="12">
          <TextInput
            value={hashedId}
            onChange={(e) => setHashedId(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canSubmit) {
                lookupMutation.mutate();
              }
            }}
            placeholder="Paste hashed Amplitude user id"
            isInvalid={lookupMutation.isError}
          />
          <Box style={{display: 'flex', gap: 12, flexWrap: 'wrap'}}>
            <Button
              variant="primary"
              onClick={() => lookupMutation.mutate()}
              isLoading={lookupMutation.isPending}
              disabled={!canSubmit}
            >
              Look up
            </Button>
          </Box>

          {lookupMutation.error instanceof Error ? (
            <SystemBanner tone="alert">
              <Text>{lookupMutation.error.message}</Text>
            </SystemBanner>
          ) : null}
        </Stack>
      </Box>

      {result ? <LookupResult result={result} /> : null}
    </Stack>
  );
};

export const AdminAmplitudeLookup = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();

  useDocumentTitle('Amplitude user lookup');

  if (!pulseUser) {
    return null;
  }

  if (!isSuperUser(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Amplitude user lookup', to: ROUTE_PATHS.ADMIN_AMPLITUDE_LOOKUP},
      ]}
    >
      <Stack gap="24">
        <Title size={24}>Amplitude user lookup</Title>
        <AmplitudeLookupPanel />
      </Stack>
    </Layout.Container>
  );
};
